import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth, signInWithEmailAndPassword, setPersistence, browserSessionPersistence } from 'firebase/auth';
import { isIpAllowed } from '../../utils/ipUtils';
import { writeAuditLog } from '../../lib/auditLogger';
import { apiClient, publicApiClient } from '../../apiClient';
import SEO from '../../components/common/SEO';

const AdminLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [step, setStep] = useState(1); // 1: Password Login, 2: OTP Verification
  const [pendingUser, setPendingUser] = useState(null);
  const [error, setError] = useState(null);
  const [infoMessage, setInfoMessage] = useState(null);
  const [ipAllowed, setIpAllowed] = useState(true);
  const [ipChecking, setIpChecking] = useState(true);
  const [userIp, setUserIp] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  const navigate = useNavigate();
  const auth = getAuth();

  useEffect(() => {
    let timer;
    if (resendCooldown > 0) {
      timer = setInterval(() => {
        setResendCooldown((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [resendCooldown]);

  useEffect(() => {
    const checkIpRestriction = async () => {
      const allowedIpsConfig = process.env.REACT_APP_ALLOWED_ADMIN_IPS;

      if (!allowedIpsConfig || allowedIpsConfig.trim() === '' || allowedIpsConfig.trim() === '*') {
        setIpAllowed(true);
        setIpChecking(false);
        return;
      }

      try {
        let fetchedIp = '';
        try {
          const res = await fetch('https://api4.ipify.org?format=json');
          if (res.ok) {
            const data = await res.json();
            fetchedIp = data.ip;
          }
        } catch (err) {
          const res = await fetch('https://api.ipify.org?format=json');
          if (res.ok) {
            const data = await res.json();
            fetchedIp = data.ip;
          }
        }

        if (!fetchedIp) {
          setIpAllowed(false);
          setIpChecking(false);
          return;
        }

        setUserIp(fetchedIp);
        const allowed = isIpAllowed(fetchedIp, allowedIpsConfig);
        setIpAllowed(allowed);
      } catch (err) {
        console.error('IP address verification error:', err);
        setIpAllowed(false);
      } finally {
        setIpChecking(false);
      }
    };

    checkIpRestriction();
  }, []);

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    setError(null);
    setInfoMessage(null);
    setIsSubmitting(true);

    try {
      try {
        const preCheck = await publicApiClient.post('/v1/auth/pre-login-check', { email });
        const checkResult = await preCheck.json();
        if (!preCheck.ok) {
          setError(checkResult.message || 'ログインに失敗しました。');
          setIsSubmitting(false);
          return;
        }
      } catch (checkErr) {
        console.error("Security pre-check error:", checkErr);
      }

      await setPersistence(auth, browserSessionPersistence);
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Call API to generate and send OTP if required
      const response = await apiClient.post('/v1/auth/otp/generate', {
        uid: user.uid,
        email: user.email
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 429) {
          setError(data.message || 'ワンタイムパスワードの再送信制限中です。');
          setStep(2);
          setPendingUser(user);
          return;
        }
        throw new Error(data.message || 'OTP Generation failed');
      }

      if (data.requiresOtp) {
        setPendingUser(user);
        setStep(2);
        setResendCooldown(60);
        setInfoMessage(`${user.email} にワンタイムパスワード（6桁）を送信しました。`);
      } else {
        // No OTP required
        try {
          await publicApiClient.post('/v1/auth/login-success', { email: user.email });
        } catch (resetErr) {
          console.error("Error resetting login failures:", resetErr);
        }

        sessionStorage.setItem('adminSessionVerified', 'true');
        await writeAuditLog({
          action: 'SYSTEM_LOGIN',
          resourceType: 'system_admin',
          resourceId: user.uid,
          metadata: { source: 'admin_console' }
        });
        navigate('/admin');
      }
    } catch (err) {
      console.error("Admin Login error:", err);
      try {
        await publicApiClient.post('/v1/auth/login-failure', { email });
      } catch (failErr) {
        console.error("Error tracking login failure:", failErr);
      }
      setError(err.message || 'ログインに失敗しました。メールアドレスとパスワードをご確認ください。');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (!pendingUser) return;

    setError(null);
    setInfoMessage(null);
    setIsSubmitting(true);

    try {
      const response = await apiClient.post('/v1/auth/otp/verify', {
        uid: pendingUser.uid,
        code: otpCode.trim()
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'ワンタイムパスワードの認証に失敗しました。');
      }

      try {
        await publicApiClient.post('/v1/auth/login-success', { email: pendingUser.email });
      } catch (resetErr) {
        console.error("Error resetting login failures:", resetErr);
      }

      sessionStorage.setItem('adminSessionVerified', 'true');
      await writeAuditLog({
        action: 'SYSTEM_LOGIN_2FA',
        resourceType: 'system_admin',
        resourceId: pendingUser.uid,
        metadata: { source: 'admin_console' }
      });

      navigate('/admin');
    } catch (err) {
      console.error("OTP Verification error:", err);
      setError(err.message || 'ワンタイムパスワードの認証に失敗しました。');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendOtp = async () => {
    if (!pendingUser || resendCooldown > 0) return;

    setError(null);
    setInfoMessage(null);
    setIsSubmitting(true);

    try {
      const response = await apiClient.post('/v1/auth/otp/generate', {
        uid: pendingUser.uid,
        email: pendingUser.email
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'ワンタイムパスワードの再送信に失敗しました。');
      }

      setResendCooldown(60);
      setInfoMessage('ワンタイムパスワードを再送信しました。');
    } catch (err) {
      console.error("Resend OTP error:", err);
      setError(err.message || '再送信に失敗しました。');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (ipChecking) {
    return (
      <>
        <SEO title="System Admin Login" description="System Admin Login" robots="noindex, nofollow" path="/admin/login" />
        <div className="min-h-screen flex items-center justify-center bg-gray-100">
          <div className="bg-white p-8 rounded-lg shadow-md w-full max-w-md text-center">
            <p className="text-gray-600">アクセス権限を確認中...</p>
          </div>
        </div>
      </>
    );
  }

  if (!ipAllowed) {
    return (
      <>
        <SEO title="System Admin Login" description="System Admin Login" robots="noindex, nofollow" path="/admin/login" />
        <div className="min-h-screen flex items-center justify-center bg-gray-100">
          <div className="bg-white p-8 rounded-lg shadow-md w-full max-w-md text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
              <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-gray-800 mb-2">アクセスが拒否されました</h2>
            <p className="text-sm text-gray-600 mb-4">
              このIPアドレスからの管理者ログイン画面へのアクセスは許可されていません。
            </p>
            {userIp && (
              <p className="text-xs text-gray-500 bg-gray-50 p-2 rounded border border-gray-200 font-mono">
                IP Address: {userIp}
              </p>
            )}
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <SEO title="System Admin Login" description="System Admin Login" robots="noindex, nofollow" path="/admin/login" />
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="bg-white p-8 rounded-lg shadow-md w-full max-w-md">
          <h1 className="text-2xl font-bold mb-6 text-center text-gray-800">
            {step === 1 ? 'System Admin Login' : '二要素認証 (2FA)'}
          </h1>

          {infoMessage && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 text-blue-700 text-sm rounded">
              {infoMessage}
            </div>
          )}

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded">
              {error}
            </div>
          )}

          {step === 1 ? (
            <form onSubmit={handlePasswordLogin} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="admin@example.com"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="••••••••"
                  required
                />
              </div>
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                {isSubmitting ? 'ログイン中...' : 'Login'}
              </button>
            </form>
          ) : (
            <form onSubmit={handleVerifyOtp} className="space-y-4">
              <p className="text-sm text-gray-600 mb-2">
                登録されたメールアドレス宛に送信された6桁のワンタイムパスワードを入力してください。（有効期限 5分）
              </p>
              <div>
                <label className="block text-sm font-medium text-gray-700">ワンタイムパスワード (6桁)</label>
                <input
                  type="text"
                  maxLength={6}
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-center text-2xl tracking-widest font-mono focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="123456"
                  required
                  autoFocus
                />
              </div>
              <button
                type="submit"
                disabled={isSubmitting || otpCode.length !== 6}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                {isSubmitting ? '検証中...' : '認証する'}
              </button>

              <div className="pt-2 text-center">
                <button
                  type="button"
                  onClick={handleResendOtp}
                  disabled={resendCooldown > 0 || isSubmitting}
                  className="text-xs text-blue-600 hover:underline disabled:text-gray-400 disabled:no-underline"
                >
                  {resendCooldown > 0 ? `ワンタイムパスワードを再送信 (${resendCooldown}s)` : 'ワンタイムパスワードを再送信'}
                </button>
              </div>

              <div className="text-center pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setStep(1);
                    setPendingUser(null);
                    setOtpCode('');
                    setError(null);
                    setInfoMessage(null);
                  }}
                  className="text-xs text-gray-500 hover:underline"
                >
                  ← ログイン画面に戻る
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </>
  );
};

export default AdminLogin;
